import { useMemo } from 'react';

import { meros } from 'meros/browser';
import { RelayEnvironmentProvider } from 'react-relay';
import {
  Environment,
  GraphQLSingularResponse,
  type IEnvironment,
  MissingFieldHandler,
  Network,
  Observable,
  RecordSource,
  RequestParameters,
  ROOT_TYPE,
  Store,
  Variables,
} from 'relay-runtime';

import { getPinnedOrganizationId } from '@watershed/shared-frontend/utils/pinnedOrganizationId';
import { HeaderKeys } from '@watershed/shared-universal/utils/constants';

type DeferResponse =
  | {
      hasNext?: boolean;
      incremental: Array<GraphQLSingularResponse>;
    }
  | { data: GraphQLSingularResponse; hasNext?: boolean }
  | GraphQLSingularResponse;

type FlattenedDeferResponse = {
  data: ReadonlyArray<GraphQLSingularResponse>;
  isLast: boolean;
};

function flattenDeferResponse(
  responses: Array<DeferResponse>
): FlattenedDeferResponse {
  const formatted: ReadonlyArray<GraphQLSingularResponse> = responses.flatMap(
    (part) => {
      if ('incremental' in part) {
        return part.incremental;
      }

      return [part];
    }
  );

  const anyOfTheResponsesAreTheLast = responses.some((response) => {
    // This query is not deferred and should finish immediately.
    if (!response.hasOwnProperty('hasNext')) {
      return true;
    }

    if ('hasNext' in response) {
      return !response.hasNext;
    }

    return false;
  });

  if (anyOfTheResponsesAreTheLast) {
    const lastPart = formatted[formatted.length - 1];

    if (lastPart) {
      // Shoutout the Coinbase folks https://github.com/facebook/relay/issues/3904
      lastPart.extensions = { is_final: true };
    }
  }

  return { data: formatted, isLast: anyOfTheResponsesAreTheLast };
}

const fetchFn = (operation: RequestParameters, variables: Variables) => {
  const organizationId = getPinnedOrganizationId();

  return Observable.create((sink) => {
    async function fetchFn() {
      // eslint-disable-next-line @watershed/must-use-wsfetch
      const response = await fetch(
        operation.name ? `/graphql/${operation.name}` : '/graphql',
        {
          method: 'POST',
          headers: {
            accept: 'multipart/mixed; deferSpec=20220824, application/json',
            'content-type': 'application/json',
            // If we use in admin, may not have pinned org id
            ...(organizationId
              ? { [HeaderKeys.activeOrganizationId]: organizationId }
              : {}),
          },
          body: JSON.stringify({
            query: operation.text,
            variables,
          }),
          credentials: 'same-origin',
        }
      );

      // meros does all the heavy lifting of parsing the multipart response
      // and returns either a Response or an AsyncGenerator of chunks
      const chunks = await meros(response, { multiple: true });

      if (chunks instanceof Response) {
        sink.next(await chunks.json());
        sink.complete();
        return;
      }

      for await (const parts of chunks) {
        const jsonPart = flattenDeferResponse(
          parts.map((part) =>
            typeof part.body === 'string' ? JSON.parse(part.body) : part.body
          )
        );
        jsonPart.data.forEach((part) => {
          sink.next(part);
        });

        if (jsonPart.isLast) {
          sink.complete();
        }
      }
    }

    void fetchFn();
  });
};

// Define fields on the query type which point to a record by id
// The key is the field name and the value is the argument name
// This is used to help Relay determine whether a entity lookup by ID can
// be satisfied by a record that's already in the cache
const RECORD_BY_ID_QUERY_FIELDS: Record<string, string> = {
  flexibleReport: 'id',
  reportingBiQuery: 'id',
  flexibleReportRequirementResponse: 'id',
} as const;

// Missing field handlers let us tell Relay about how to determine the record
// ID of that fields point to prior to actually fetching it. This means that
// in some cases we can treat a new field selection as a cache hit if it points
// to a record that's already in the cache.
const missingFieldHandlers: Array<MissingFieldHandler> = [
  {
    handle: (field, record, args) => {
      if (record === null || record === undefined) {
        return undefined;
      }

      // Handle record-by-id fields on the Query type
      if (record.getType() === ROOT_TYPE) {
        if (
          RECORD_BY_ID_QUERY_FIELDS[field.name] &&
          args.hasOwnProperty(RECORD_BY_ID_QUERY_FIELDS[field.name])
        ) {
          return args[RECORD_BY_ID_QUERY_FIELDS[field.name]];
        }
      }
    },
    kind: 'linked',
  },
];

export function createEnvironment() {
  const network = Network.create(fetchFn);
  const store = new Store(new RecordSource());
  return new Environment({ store, network, missingFieldHandlers });
}
export default function RelayEnvironment({
  children,
  environment,
}: {
  environment?: IEnvironment;
  children: React.ReactNode;
}): React.ReactElement {
  const relayEnvironment = useMemo(() => {
    return environment || createEnvironment();
  }, [environment]);

  return (
    <RelayEnvironmentProvider environment={relayEnvironment}>
      {children}
    </RelayEnvironmentProvider>
  );
}
